import { memo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ArrowLeftRight, BarChart3, CreditCard, Target, PiggyBank, Settings, Menu, X, Clock, Tags, Moon, Sun, Monitor, Calendar, Landmark, Shield, Wallet, ChartLine, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/contexts/SidebarContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useRole } from '@/contexts/RoleContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useHoverPreload } from '@/hooks/useHoverPreload';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
// Standalone menu items (not in groups)
const standaloneItems = [{
  id: 'dashboard',
  title: 'Visão geral',
  icon: LayoutDashboard,
  path: '/dashboard'
}];

// Menu groups with submenus
const menuGroups = [{
  id: 'financeiro',
  title: 'Financeiro',
  icon: Wallet,
  items: [{
    id: 'transacoes',
    title: 'Transações',
    icon: ArrowLeftRight,
    path: '/transacoes'
  }, {
    id: 'contas',
    title: 'Contas',
    icon: Landmark,
    path: '/contas'
  }, {
    id: 'cartoes',
    title: 'Cartões',
    icon: CreditCard,
    path: '/cartoes'
  }, {
    id: 'lancamentos',
    title: 'Lançamentos',
    icon: Clock,
    path: '/lancamentos'
  }]
}];

// Standalone item between groups
const relatoriosItem = {
  id: 'relatorios',
  title: 'Relatórios',
  icon: BarChart3,
  path: '/relatorios'
};

// Second group of menus
const secondMenuGroup = [{
  id: 'planejamento',
  title: 'Planejamento',
  icon: ChartLine,
  items: [{
    id: 'agenda',
    title: 'Agenda',
    icon: Calendar,
    path: '/agenda'
  }, {
    id: 'orcamentos',
    title: 'Orçamentos',
    icon: PiggyBank,
    path: '/orcamentos'
  }, {
    id: 'metas',
    title: 'Metas',
    icon: Target,
    path: '/metas'
  }, {
    id: 'categorias',
    title: 'Categorias',
    icon: Tags,
    path: '/categorias'
  }]
}];
// Memoized Sidebar to prevent unnecessary re-renders
export const Sidebar = memo(() => {
  const {
    isCollapsed,
    isMobileOpen,
    expandedGroups,
    setIsMobileOpen,
    toggleSidebar,
    toggleGroup,
    expandGroup
  } = useSidebar();
  const location = useLocation();
  const {
    theme,
    setTheme,
    actualTheme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isAdmin
  } = useRole();
  const {
    getPreloadHandlers
  } = useHoverPreload({
    delay: 50
  });
  const ThemeIcon = theme === 'dark' ? Sun : theme === 'light' ? Moon : Monitor;
  const cycleTheme = () => {
    const themes = ['light', 'dark', 'system'] as const;
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };
  return <>
      {/* Mobile Overlay */}
      {isMobileOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setIsMobileOpen(false)} />}

      {/* Sidebar */}
      <aside data-sidebar="sidebar" data-mobile={isMobileOpen ? "true" : "false"} data-state={isCollapsed ? "collapsed" : "expanded"} className={cn("fixed left-0 top-0 h-[calc(100%-4rem)] lg:h-full bg-card border-r border-border z-40 transition-all duration-300 ease-in-out",
    // Desktop widths
    "lg:w-64",
    // Default width
    isCollapsed && "lg:w-16",
    // Collapsed width
    // Mobile behavior
    "w-64",
    // Mobile width when open
    "lg:translate-x-0",
    // Always visible on desktop
    isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0" // Mobile overlay behavior
    )}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 bg-primary">
            <div className="flex items-center justify-between">
              {!isCollapsed && <div className="flex items-center pl-4">
                  <img src={actualTheme === 'dark' ? '/lovable-uploads/25c6a08b-9234-4abc-b51f-4c0c45f858ea.png' : '/lovable-uploads/25c6a08b-9234-4abc-b51f-4c0c45f858ea.png'} alt="Planific Logo" className="h-7 w-auto object-contain" />
                </div>}
              {/* Desktop and Mobile Toggle Button */}
              <button data-sidebar="trigger" onClick={toggleSidebar} className="p-2 rounded-xl transition-colors ml-auto">
                {isMobileOpen ? <X size={16} className="text-primary-foreground" /> : <Menu size={16} className="text-primary-foreground" />}
              </button>
            </div>
          </div>


          {/* Navigation with ScrollArea */}
          <ScrollArea className="flex-1">
            <nav className="p-3 space-y-1">
              {/* Standalone items */}
              {standaloneItems.map(item => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return <Link key={item.id} to={item.path} onClick={() => setIsMobileOpen(false)} data-tutorial-id={`sidebar-${item.id}`} className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ease-out",
                "hover:scale-[1.02] active:scale-[0.98]",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                  : "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-md hover:shadow-accent/20",
                isCollapsed && "justify-center px-2"
              )} title={isCollapsed ? t(`navigation.${item.id}`, item.title) : undefined} {...getPreloadHandlers(item.path)}>
                    <Icon size={18} className={cn(
                      "flex-shrink-0 transition-all duration-200",
                      isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                    )} />
                    {!isCollapsed && <span className="font-medium text-sm">
                        {t(`navigation.${item.id}`, item.title)}
                      </span>}
                  </Link>;
            })}

              {/* Section Label */}
              {!isCollapsed && <div className="pt-4 pb-2 px-3">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Menu</span>
              </div>}
              {isCollapsed && <Separator className="my-3 bg-border/40" />}

              {/* Grouped items with collapsible submenus */}
              {menuGroups.map(group => {
              const hasActiveItem = group.items.some(item => location.pathname === item.path);
              const isExpanded = expandedGroups.has(group.id);
              const GroupIcon = group.icon;

              // Auto-expand group if contains active route
              useEffect(() => {
                if (hasActiveItem && !isExpanded) {
                  expandGroup(group.id);
                }
              }, [location.pathname]);
              return <Collapsible key={group.id} open={isCollapsed ? true : isExpanded} onOpenChange={() => !isCollapsed && toggleGroup(group.id)}>
                    {/* Group trigger - only visible when sidebar is not collapsed */}
                    {!isCollapsed && <CollapsibleTrigger className="w-full" data-tutorial-id={`sidebar-group-${group.id}`}>
                        <div className={cn(
                          "group flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ease-out",
                          "hover:scale-[1.01] active:scale-[0.99]",
                          "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-sm",
                          hasActiveItem && "text-foreground bg-accent/50 shadow-sm"
                        )}>
                          <div className="flex items-center gap-3">
                            <GroupIcon size={18} className={cn(
                              "flex-shrink-0 transition-all duration-200",
                              hasActiveItem ? "text-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                            )} />
                            <span className="font-medium text-sm">{group.title}</span>
                          </div>
                          <ChevronDown size={14} className={cn(
                            "text-muted-foreground transition-transform duration-300 ease-out",
                            isExpanded && "rotate-180"
                          )} />
                        </div>
                      </CollapsibleTrigger>}

                    {/* Submenu items */}
                    <CollapsibleContent>
                      <div className={cn("space-y-0.5", !isCollapsed && "ml-3 pl-3 mt-1 border-l border-border/40")}>
                        {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon;
                      return <Link key={item.id} to={item.path} onClick={() => setIsMobileOpen(false)} data-tutorial-id={`sidebar-${item.id}`} className={cn(
                                "group flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 ease-out",
                                "hover:scale-[1.02] active:scale-[0.98]",
                                isActive 
                                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" 
                                  : "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-sm hover:shadow-accent/15",
                                isCollapsed && "justify-center px-2"
                              )} title={isCollapsed ? t(`navigation.${item.id}`, item.title) : undefined} {...getPreloadHandlers(item.path)}>
                                <Icon size={16} className={cn(
                                  "flex-shrink-0 transition-all duration-200",
                                  isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                                )} />
                                {!isCollapsed && <span className="text-sm">
                                    {t(`navigation.${item.id}`, item.title)}
                                  </span>}
                              </Link>;
                    })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>;
            })}

              {/* Second group - Planejamento */}
              {secondMenuGroup.map(group => {
              const hasActiveItem = group.items.some(item => location.pathname === item.path);
              const isExpanded = expandedGroups.has(group.id);
              const GroupIcon = group.icon;

              // Auto-expand group if contains active route
              useEffect(() => {
                if (hasActiveItem && !isExpanded) {
                  expandGroup(group.id);
                }
              }, [location.pathname]);
              return <Collapsible key={group.id} open={isCollapsed ? true : isExpanded} onOpenChange={() => !isCollapsed && toggleGroup(group.id)}>
                    {/* Group trigger - only visible when sidebar is not collapsed */}
                    {!isCollapsed && <CollapsibleTrigger className="w-full" data-tutorial-id={`sidebar-group-${group.id}`}>
                        <div className={cn(
                          "group flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ease-out",
                          "hover:scale-[1.01] active:scale-[0.99]",
                          "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-sm",
                          hasActiveItem && "text-foreground bg-accent/50 shadow-sm"
                        )}>
                          <div className="flex items-center gap-3">
                            <GroupIcon size={18} className={cn(
                              "flex-shrink-0 transition-all duration-200",
                              hasActiveItem ? "text-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                            )} />
                            <span className="font-medium text-sm">{group.title}</span>
                          </div>
                          <ChevronDown size={14} className={cn(
                            "text-muted-foreground transition-transform duration-300 ease-out",
                            isExpanded && "rotate-180"
                          )} />
                        </div>
                      </CollapsibleTrigger>}

                    {/* Submenu items */}
                    <CollapsibleContent>
                      <div className={cn("space-y-0.5", !isCollapsed && "ml-3 pl-3 mt-1 border-l border-border/40")}>
                        {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon;
                      return <Link key={item.id} to={item.path} onClick={() => setIsMobileOpen(false)} data-tutorial-id={`sidebar-${item.id}`} className={cn(
                                "group flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 ease-out",
                                "hover:scale-[1.02] active:scale-[0.98]",
                                isActive 
                                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" 
                                  : "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-sm hover:shadow-accent/15",
                                isCollapsed && "justify-center px-2"
                              )} title={isCollapsed ? t(`navigation.${item.id}`, item.title) : undefined} {...getPreloadHandlers(item.path)}>
                                <Icon size={16} className={cn(
                                  "flex-shrink-0 transition-all duration-200",
                                  isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                                )} />
                                {!isCollapsed && <span className="text-sm">
                                    {t(`navigation.${item.id}`, item.title)}
                                  </span>}
                              </Link>;
                    })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>;
            })}

              {/* Section Label for Relatórios */}
              {!isCollapsed && <div className="pt-4 pb-2 px-3">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Análises</span>
              </div>}
              {isCollapsed && <Separator className="my-3 bg-border/40" />}

              {/* Relatórios standalone item */}
              {(() => {
              const isActive = location.pathname === relatoriosItem.path;
              return <Link to={relatoriosItem.path} onClick={() => setIsMobileOpen(false)} data-tutorial-id={`sidebar-${relatoriosItem.id}`} className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ease-out",
                "hover:scale-[1.02] active:scale-[0.98]",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                  : "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-md hover:shadow-accent/20",
                isCollapsed && "justify-center px-2"
              )} title={isCollapsed ? t(`navigation.${relatoriosItem.id}`, relatoriosItem.title) : undefined} {...getPreloadHandlers(relatoriosItem.path)}>
                  <relatoriosItem.icon size={18} className={cn(
                    "flex-shrink-0 transition-all duration-200",
                    isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                  )} />
                  {!isCollapsed && <span className="font-medium text-sm">
                      {t(`navigation.${relatoriosItem.id}`, relatoriosItem.title)}
                    </span>}
                </Link>;
            })()}

              {/* Admin Link - Only show for admins */}
              {isAdmin && <>
                {!isCollapsed && <div className="pt-4 pb-2 px-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Sistema</span>
                </div>}
                {isCollapsed && <Separator className="my-3 bg-border/40" />}
                <Link to="/admin" onClick={() => setIsMobileOpen(false)} className={cn(
                  "group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ease-out",
                  "hover:scale-[1.02] active:scale-[0.98]",
                  location.pathname === '/admin' 
                    ? "bg-destructive text-destructive-foreground shadow-lg shadow-destructive/25" 
                    : "text-muted-foreground hover:text-foreground hover:bg-accent hover:shadow-md hover:shadow-accent/20",
                  isCollapsed && "justify-center px-2"
                )} title={isCollapsed ? "Administração" : undefined}>
                  <Shield size={18} className={cn(
                    "flex-shrink-0 transition-all duration-200",
                    location.pathname === '/admin' ? "text-destructive-foreground" : "text-muted-foreground group-hover:text-foreground group-hover:scale-110"
                  )} />
                  {!isCollapsed && <span className="font-medium text-sm">Administração</span>}
                </Link>
              </>}
            </nav>
          </ScrollArea>

          {/* Theme Toggle & Footer */}
          <div className="p-4 space-y-3">
            <Button onClick={cycleTheme} variant="ghost" size="sm" className={cn("w-full rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800", isCollapsed ? "justify-center px-3" : "justify-start")} title={isCollapsed ? t(`theme.${theme}`, theme) : undefined}>
              <ThemeIcon size={16} className="flex-shrink-0 min-w-[16px] min-h-[16px]" />
              {!isCollapsed && <span className="ml-3 text-base capitalize">{t(`theme.${theme}`, theme)}</span>}
            </Button>
            
            {!isCollapsed && <div className="text-xs text-muted-foreground text-center">Planific v2.0</div>}
          </div>
        </div>
      </aside>
    </>;
});